import React, { useEffect, useRef, useState } from 'react';

export default function SplashScreen({ ready, onDone }) {
  const [hiding, setHiding] = useState(false);
  const animDone = useRef(false);
  const prodDone = useRef(false);

  const tryClose = () => {
    if (!animDone.current || !prodDone.current) return;
    setHiding(true);
    setTimeout(onDone, 500);
  };

  useEffect(() => {
    const t = setTimeout(() => { animDone.current = true; tryClose(); }, 3200);
    return () => clearTimeout(t);
  }, []); // eslint-disable-line

  useEffect(() => {
    if (!ready) return;
    prodDone.current = true;
    tryClose();
  }, [ready]); // eslint-disable-line

  return (
    <div style={{ ...s.overlay, opacity: hiding ? 0 : 1, transition: hiding ? 'opacity 0.5s ease' : 'none' }}>
      <style>{css}</style>
      <svg id="logo-svg" width="320" height="160" viewBox="0 0 320 160" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <clipPath id="lclip"><ellipse cx="88" cy="80" rx="72" ry="62" /></clipPath>
          <clipPath id="rclip"><ellipse cx="232" cy="80" rx="72" ry="62" /></clipPath>
          <radialGradient id="irisGradL" cx="45%" cy="40%" r="60%">
            <stop offset="0%" stopColor="#90caf9" />
            <stop offset="60%" stopColor="#4361ee" />
            <stop offset="100%" stopColor="#1a237e" />
          </radialGradient>
          <radialGradient id="irisGradR" cx="45%" cy="40%" r="60%">
            <stop offset="0%" stopColor="#90caf9" />
            <stop offset="60%" stopColor="#4361ee" />
            <stop offset="100%" stopColor="#1a237e" />
          </radialGradient>
          <filter id="glow">
            <feGaussianBlur stdDeviation="3" result="blur" />
            <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
          </filter>
          <filter id="glowStrong">
            <feGaussianBlur stdDeviation="6" result="blur" />
            <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
          </filter>
        </defs>

        {/* contour infini */}
        <path className="inf-path"
          d="M 160,80 C 155,55 135,25 100,18 C 65,11 20,35 20,80 C 20,125 65,149 100,142 C 135,135 155,105 160,80 C 165,55 185,25 220,18 C 255,11 300,35 300,80 C 300,125 255,149 220,142 C 185,135 165,105 160,80 Z"
          fill="none" stroke="#4361ee" strokeWidth="28" strokeLinecap="round" strokeLinejoin="round"
        />

        {/* OEIL GAUCHE */}
        <g className="eye-group eye-left" style={{ transformOrigin: '88px 80px' }}>
          {/* halo extérieur pulsant */}
          <ellipse className="eye-halo" cx="88" cy="80" rx="50" ry="50" fill="none" stroke="#4361ee" strokeWidth="2" opacity="0" clipPath="url(#lclip)" />
          {/* blanc de l'oeil */}
          <ellipse cx="88" cy="80" rx="46" ry="46" fill="white" clipPath="url(#lclip)" />
          {/* iris avec dégradé */}
          <ellipse className="iris-l" cx="88" cy="80" rx="32" ry="32" fill="url(#irisGradL)" clipPath="url(#lclip)" />
          {/* pupille */}
          <ellipse className="pupil-l" cx="88" cy="80" rx="14" ry="14" fill="#050d2e" clipPath="url(#lclip)" />
          {/* reflet principal */}
          <circle className="reflet-l" cx="98" cy="68" r="9" fill="white" opacity="0.9" clipPath="url(#lclip)" />
          {/* petit reflet */}
          <circle cx="82" cy="88" r="4" fill="white" opacity="0.35" clipPath="url(#lclip)" />
          {/* anneau iris brillant */}
          <ellipse cx="88" cy="80" rx="32" ry="32" fill="none" stroke="rgba(255,255,255,0.25)" strokeWidth="2" clipPath="url(#lclip)" />
        </g>

        {/* OEIL DROIT */}
        <g className="eye-group eye-right" style={{ transformOrigin: '232px 80px' }}>
          <ellipse className="eye-halo" cx="232" cy="80" rx="50" ry="50" fill="none" stroke="#4361ee" strokeWidth="2" opacity="0" clipPath="url(#rclip)" />
          <ellipse cx="232" cy="80" rx="46" ry="46" fill="white" clipPath="url(#rclip)" />
          <ellipse className="iris-r" cx="232" cy="80" rx="32" ry="32" fill="url(#irisGradR)" clipPath="url(#rclip)" />
          <ellipse className="pupil-r" cx="232" cy="80" rx="14" ry="14" fill="#050d2e" clipPath="url(#rclip)" />
          <circle className="reflet-r" cx="242" cy="68" r="9" fill="white" opacity="0.9" clipPath="url(#rclip)" />
          <circle cx="226" cy="88" r="4" fill="white" opacity="0.35" clipPath="url(#rclip)" />
          <ellipse cx="232" cy="80" rx="32" ry="32" fill="none" stroke="rgba(255,255,255,0.25)" strokeWidth="2" clipPath="url(#rclip)" />
        </g>
      </svg>
    </div>
  );
}

const s = {
  overlay: {
    position: 'fixed', inset: 0, zIndex: 9999,
    background: 'white',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
};

const css = `
  /* ── CONTOUR INFINI ── */
  #logo-svg .inf-path {
    stroke-dasharray: 900;
    stroke-dashoffset: 900;
    animation: drawInf 1.8s cubic-bezier(.4,0,.2,1) forwards;
    filter: drop-shadow(0 0 8px #4361eeaa);
  }
  @keyframes drawInf { to { stroke-dashoffset: 0; } }

  /* ── APPARITION DES YEUX ── */
  .eye-group {
    opacity: 0;
    animation: popEye 0.6s cubic-bezier(.34,1.56,.64,1) forwards;
  }
  .eye-left  { animation-delay: 1.85s; }
  .eye-right { animation-delay: 2.1s;  }
  @keyframes popEye {
    0%   { opacity: 0; transform: scale(0) rotate(-30deg); }
    60%  { opacity: 1; transform: scale(1.18) rotate(6deg); }
    80%  { transform: scale(0.94) rotate(-3deg); }
    100% { opacity: 1; transform: scale(1) rotate(0deg); }
  }

  /* ── HALO PULSANT ── */
  .eye-halo {
    animation: haloEye 1.2s ease-out 2.4s infinite;
  }
  @keyframes haloEye {
    0%   { opacity: 0.7; r: 34; stroke-width: 3; }
    100% { opacity: 0;   r: 56; stroke-width: 0; }
  }

  /* ── IRIS ROTATION ── */
  .iris-l {
    transform-origin: 88px 80px;
    animation: spinIris 6s linear 2.4s infinite;
  }
  .iris-r {
    transform-origin: 232px 80px;
    animation: spinIris 6s linear 2.4s infinite reverse;
  }
  @keyframes spinIris {
    from { transform: rotate(0deg); }
    to   { transform: rotate(360deg); }
  }

  /* ── PUPILLE DILATATION ── */
  .pupil-l {
    transform-origin: 88px 80px;
    animation: pupilPulse 2s ease-in-out 2.4s infinite;
  }
  .pupil-r {
    transform-origin: 232px 80px;
    animation: pupilPulse 2s ease-in-out 2.6s infinite;
  }
  @keyframes pupilPulse {
    0%,100% { transform: scale(1); }
    50%     { transform: scale(1.4); }
  }

  /* ── REGARD GAUCHE/DROITE ── */
  .reflet-l {
    transform-origin: 88px 80px;
    animation: lookAround 4s ease-in-out 2.5s infinite;
  }
  .reflet-r {
    transform-origin: 232px 80px;
    animation: lookAround 4s ease-in-out 2.7s infinite;
  }
  @keyframes lookAround {
    0%,100% { transform: translate(0px, 0px); }
    25%     { transform: translate(-6px, 3px); }
    50%     { transform: translate(0px, -4px); }
    75%     { transform: translate(6px, 3px); }
  }
`;
